// Shared identify/group logic for both fresh Auth0 logins and restored sessions
// (returning users whose AUTH cookie is still valid). Both paths must call
// identify() and group() so Segment receives traits for every active user,
// not just users who complete a fresh login popup.

import { identifyUser, groupAccount } from './tracking'
import type { TrackingContext } from './tracking'
import type { UserRole } from './types'

export interface StoredAuthUser {
  id: string
  email: string
  firstName?: string | null
  lastName?: string | null
  createdAt: string
  superuser?: boolean | null
  accountRoles?: ReadonlyArray<{ readonly role: string }> | null
  account: {
    id: string
    name: string
    createdAt: string
  }
}

function deriveHighestRole(accountRoles: ReadonlyArray<{ readonly role: string }>): string {
  const priority: Record<string, number> = { ADMIN: 3, APPROVER: 2, EDITOR: 1 }
  return accountRoles.reduce(
    (highest, ur) => (priority[ur.role] ?? 0) > (priority[highest] ?? 0) ? ur.role : highest,
    'EDITOR'
  )
}

function toUserRole(role: string): UserRole {
  return role.toLowerCase() as UserRole
}

/**
 * Calls identify() and group() for the given user/account. Use on fresh login
 * and on session restore (cookie-based auth) so every active session is
 * represented in Segment, regardless of how it started.
 */
export function identifyAndGroupUser(user: StoredAuthUser): TrackingContext {
  const superuser = user.superuser ?? false
  const ctx: TrackingContext = { accountId: user.account.id, superuser }

  identifyUser(
    user.id,
    {
      email: user.email ?? '',
      first_name: user.firstName ?? undefined,
      last_name: user.lastName ?? undefined,
      account_id: user.account.id,
      role: toUserRole(deriveHighestRole(user.accountRoles ?? [])),
      created_at: user.createdAt,
      last_active_at: new Date().toISOString(),
    },
    superuser
  )

  groupAccount(
    {
      group_type: 'account',
      name: user.account.name,
      created_at: user.account.createdAt,
      last_active_at: new Date().toISOString(),
    },
    ctx
  )

  return ctx
}
